/**
 * Generic OAuth Callback Page
 *
 * Handles OAuth redirects from various providers.
 * Extracts the authorization code and sends it to the parent window.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import './GoogleOAuthCallback.css'; // Reuse existing styles

type CallbackStatus = 'processing' | 'success' | 'error';

interface OAuthCallbackProps {
  provider: 'microsoft' | 'dropbox' | 'slack' | 'notion';
}

const providerNames: Record<string, string> = {
  microsoft: 'Microsoft',
  dropbox: 'Dropbox',
  slack: 'Slack',
  notion: 'Notion',
};

const providerColors: Record<string, string> = {
  microsoft: '#00a4ef',
  dropbox: '#0061ff',
  slack: '#4a154b',
  notion: '#000000',
};

export function OAuthCallback({ provider }: OAuthCallbackProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle error from provider
    if (error) {
      setStatus('error');
      setErrorMessage(errorDescription || error);

      // Send error to parent if in popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: `${provider}-oauth-callback`,
            error: errorDescription || error,
          },
          window.location.origin
        );
        setTimeout(() => window.close(), 2000);
      }
      return;
    }

    // Validate required params
    if (!code) {
      setStatus('error');
      setErrorMessage('Codice di autorizzazione mancante');
      return;
    }

    // Send code to parent window if in popup
    if (window.opener) {
      window.opener.postMessage(
        {
          type: `${provider}-oauth-callback`,
          code,
          state,
        },
        window.location.origin
      );
      setStatus('success');

      // Close popup after short delay
      setTimeout(() => window.close(), 1500);
    } else {
      // Not in popup - redirect to settings with params
      const params = new URLSearchParams();
      params.set(`${provider}_code`, code);
      if (state) params.set(`${provider}_state`, state);
      navigate(`/settings/integrations?${params.toString()}`, {
        replace: true,
      });
    }
  }, [searchParams, navigate, provider]);

  const spinnerColor = providerColors[provider] || '#4285f4';
  const providerName = providerNames[provider] || provider;

  return (
    <div className="oauth-callback">
      <div className="oauth-callback-content">
        {status === 'processing' && (
          <>
            <div
              className="oauth-callback-spinner"
              style={{ borderTopColor: spinnerColor }}
            />
            <h2>Connecting...</h2>
            <p>We are completing the connection with {providerName}.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="oauth-callback-icon oauth-callback-icon--success">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Connection successful!</h2>
            <p>This window will close automatically.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="oauth-callback-icon oauth-callback-icon--error">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round"/>
              </svg>
            </div>
            <h2>Connection failed</h2>
            <p>{errorMessage || 'An error occurred during the connection.'}</p>
            {!window.opener && (
              <button
                className="oauth-callback-btn"
                style={{ background: spinnerColor }}
                onClick={() => navigate('/settings/integrations')}
              >
                Back to settings
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Named exports for each provider
export function MicrosoftOAuthCallback() {
  return <OAuthCallback provider="microsoft" />;
}

export function DropboxOAuthCallback() {
  return <OAuthCallback provider="dropbox" />;
}

export function SlackOAuthCallback() {
  return <OAuthCallback provider="slack" />;
}

export function NotionOAuthCallback() {
  return <OAuthCallback provider="notion" />;
}

export default OAuthCallback;
